'use client'

import {
  PLAYBOOK_CLOSING,
  PLAYBOOK_SECTIONS,
} from '@/lib/pre-season-playbook/content'
import { useCallback, useEffect, useRef, useState } from 'react'

export type PlaybookNavItem = {
  id: string
  title: string
  colorClass: string
  barClassName: string
}

const PLAYBOOK_NAV_ITEMS: PlaybookNavItem[] = [
  {
    id: 'six-weeks-out',
    title: PLAYBOOK_SECTIONS[0].title,
    colorClass: 'text-pea',
    barClassName: 'bg-pea',
  },
  {
    id: 'four-weeks-out',
    title: PLAYBOOK_SECTIONS[1].title,
    colorClass: 'text-grape-dark',
    barClassName: 'bg-grape-dark',
  },
  {
    id: 'two-weeks-out',
    title: PLAYBOOK_SECTIONS[2].title,
    colorClass: 'text-royal-dark',
    barClassName: 'bg-royal-dark',
  },
  {
    id: 'one-week-out',
    title: 'One week out',
    colorClass: 'text-blueberry-dark',
    barClassName: 'bg-blueberry-dark',
  },
  {
    id: 'end-of-season',
    title: 'End of season',
    colorClass: 'text-tangerine-dark',
    barClassName: 'bg-tangerine-dark',
  },
  {
    id: 'playbook-closing',
    title: PLAYBOOK_CLOSING.title,
    colorClass: 'text-plum',
    barClassName: 'bg-plum',
  },
]

const navLinkClass =
  'text-lg font-semibold leading-[1.2] transition-opacity hover:opacity-70'

const SCROLL_SPY_OFFSET = 120

type Props = {
  items?: PlaybookNavItem[]
  ariaLabel?: string
}

export default function PlaybookSidebarNav({
  items = PLAYBOOK_NAV_ITEMS,
  ariaLabel = 'Playbook sections',
}: Props) {
  const [activeId, setActiveId] = useState(items[0].id)
  const [indicator, setIndicator] = useState({ top: 0, height: 0 })
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>())
  const trackRef = useRef<HTMLDivElement>(null)

  const updateIndicator = useCallback((id: string) => {
    const link = linkRefs.current.get(id)
    const track = trackRef.current
    if (!link || !track) return

    const linkRect = link.getBoundingClientRect()
    const trackRect = track.getBoundingClientRect()
    setIndicator({
      top: linkRect.top - trackRect.top,
      height: linkRect.height,
    })
  }, [])

  const updateActiveFromScroll = useCallback(() => {
    let nextActive = items[0].id

    for (const item of items) {
      const section = document.getElementById(item.id)
      if (!section) continue
      if (section.getBoundingClientRect().top <= SCROLL_SPY_OFFSET) {
        nextActive = item.id
      }
    }

    setActiveId(nextActive)
  }, [items])

  useEffect(() => {
    updateActiveFromScroll()
    window.addEventListener('scroll', updateActiveFromScroll, { passive: true })
    window.addEventListener('resize', updateActiveFromScroll)

    return () => {
      window.removeEventListener('scroll', updateActiveFromScroll)
      window.removeEventListener('resize', updateActiveFromScroll)
    }
  }, [updateActiveFromScroll])

  useEffect(() => {
    updateIndicator(activeId)
  }, [activeId, updateIndicator])

  useEffect(() => {
    const handleResize = () => updateIndicator(activeId)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [activeId, updateIndicator])

  return (
    <nav aria-label={ariaLabel} className="mb-[60px] flex gap-3">
      <div ref={trackRef} className="relative w-1 shrink-0">
        <div
          aria-hidden="true"
          className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full bg-greyscale-linen"
        />
        <div
          aria-hidden="true"
          className={`absolute left-1/2 w-1 -translate-x-1/2 rounded-full transition-all duration-200 ${items.find((item) => item.id === activeId)?.barClassName ?? 'bg-soft-black'}`}
          style={{ top: indicator.top, height: indicator.height }}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {items.map((item) => (
          <a
            key={item.id}
            ref={(el) => {
              if (el) linkRefs.current.set(item.id, el)
              else linkRefs.current.delete(item.id)
            }}
            href={`#${item.id}`}
            aria-current={activeId === item.id ? 'location' : undefined}
            onClick={() => {
              setActiveId(item.id)
              requestAnimationFrame(() => updateIndicator(item.id))
            }}
            className={`${navLinkClass} ${item.colorClass}`}
          >
            {item.title}
          </a>
        ))}
      </div>
    </nav>
  )
}
